import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Breadcrumb, IPredicate } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import type { IDonorOption } from './TransactionFilters.types';
import { DONATION_LIST_PARAMS } from './TransactionTable.utils';

const VISIBLE_DONORS_PAGE_SIZE = 100;

/**
 * Resolve the set of donors to surface as default options in the donor filter
 * dropdown. Pulls a recent slice of donation breadcrumbs (matching every
 * filter except the donor filter itself), then resolves each unique `per_id`
 * to a Person to read the display name.
 *
 * The fetch is lazy: nothing happens until `enabled` flips to true (e.g. when
 * the filters drawer first opens), which avoids the cost on the table's
 * default render.
 *
 * IMPORTANT: The component using this hook MUST be wrapped with observer()
 * from mobx-react-lite for proper reactivity on Persons.get().
 *
 * @param predicates - Predicates to AND with the base donation predicate. Pass
 *   the predicates built from current filters minus the donor filter so the
 *   dropdown shows donors the user could add, not just those already selected.
 * @param enabled - When false, no fetch is performed and an empty array is
 *   returned. Flip to true when the donor dropdown is about to be visible.
 * @returns Donor options sorted alphabetically by name.
 */
function useVisibleDonors(predicates: IPredicate[], enabled: boolean): IDonorOption[] {
  const { Breadcrumbs, Persons } = useStore();
  const { t } = useTranslation();
  const listId = useId();
  const [response, setResponse] = useState<ListResponse<Breadcrumb> | null>(null);

  // Stable cache key — predicate array identity isn't stable across renders.
  const predicatesKey = JSON.stringify(predicates);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const next = Breadcrumbs.list(
      {
        ...DONATION_LIST_PARAMS,
        predicates: [...DONATION_LIST_PARAMS.predicates!, ...predicates],
        pagination: {
          page: 0,
          page_size: VISIBLE_DONORS_PAGE_SIZE,
        },
        ordering: ['-d_c'],
      },
      {},
      listId,
    );
    setResponse(next);
  }, [Breadcrumbs, listId, enabled, predicatesKey]);

  // Access observable fields directly so observer() picks up updates.
  const hasLoadedOnce = response?.hasLoadedOnce ?? false;
  const models = response?.models ?? [];

  if (!enabled || !hasLoadedOnce) {
    return [];
  }

  const seen = new Map<string, IDonorOption>();
  for (const breadcrumb of models) {
    const perId = breadcrumb.get('per_id');
    if (!perId || seen.has(perId)) {
      continue;
    }
    const person = Persons.get(perId);
    seen.set(perId, {
      id: perId,
      name: person.name || t('Anonymous'),
    });
  }

  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export default useVisibleDonors;
